import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useEffect, useState } from "react";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";
import { getISOWeek } from 'date-fns';

const URL_ProdDia = "/produtividade/dia";

const ProdutividadeSemana = () => {

    const apiAxios = APIAxios();
    const { sessao } = SessaoAuth();

    const [loadingProdDia, setLoadingProdDia] = useState(false);
    const [dataProdDia, setDataProdDia] = useState([]);
    const [error, setError] = useState("");
    const [datas, setDatas] = useState([]);

    const loadProdDia = async () => {

        setLoadingProdDia(true);
        setError("");

        try {

            const response = await apiAxios.post(URL_ProdDia,
                {
                    data_inicio: sessao.dataIni,
                    data_fim: sessao.dataFim,
                    id_usuario: 0,
                });

            setDataProdDia(response.data.data);

        } catch (err) {
            if (!err.response) {
                setError("Servidor não responde. Verifique informações selecionadas.");
            } else if (err.response.status === 400 || err.response.status === 401) {
                setError(err.response.data.erro);
            } else {
                setError("Erro desconhecido.");
            }
        } finally {
            setLoadingProdDia(false);
        }
    };

    useEffect(() => {
        loadProdDia();
    }, [sessao]);

    const sumarizar = async () => {

        setLoadingProdDia(true);
        setError("");

        const grouped = [...dataProdDia].reduce((acc, item) => {

            const semana = getISOWeek(new Date(item.data));

            if (!acc[semana]) {
                acc[semana] = { totalProd: 0, count: 0 };
            }
            acc[semana].totalProd += (item.minRegistrados * 100 / item.minTotal);
            acc[semana].count += 1;
            return acc;
        }, {});

        const resultado = Object.keys(grouped).map(data => ({
            semana: Number(data),
            produtividade: grouped[data].totalProd / grouped[data].count > 100 ? 100 : grouped[data].totalProd / grouped[data].count
        }));

        const sorted = resultado.sort((a, b) => {
            return a.semana - b.semana;
        });

        setDatas(sorted);

        setLoadingProdDia(false);
    };

    useEffect(() => {
        sumarizar();
    }, [dataProdDia]);

    const chartOptionsMult = {
        chart: {
            type: "bar",
            height: 350,
            toolbar: { show: false },
            background: "transparent",
        },
        colors: ["#4caf50"],
        fill: {
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.3,
                gradientToColors: ["#1b5e20"],
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 0.85,
            },
        },
        stroke: {
            width: [0],
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                dataLabels: {
                    position: "top",
                },
                columnWidth: "65%",
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => `${val.toFixed(0)}%`,
            style: {
                fontSize: "10px",
                fontWeight: 700,
                colors: ["#1b5e20"],
            },
            offsetY: -4,
        },
        grid: {
            borderColor: "#d4edda",
            strokeDashArray: 4,
            padding: { top: 0, right: 10, bottom: 0, left: 10 },
        },
        xaxis: {
            categories: datas.map((item) => `Sem. ${item.semana}`),
            position: "bottom",
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                style: {
                    fontSize: "10px",
                    colors: "#6b7a99",
                    fontWeight: 500,
                },
            },
        },
        yaxis: [
            {
                labels: {
                    formatter: (val) => `${val.toFixed(0)}%`,
                    style: { colors: "#6b7a99", fontSize: "11px" },
                },
                title: {
                    text: "Aproveitamento",
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#2e7d32",
                        fontFamily: "inherit",
                    },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
        ],
        tooltip: {
            theme: "light",
            y: { formatter: (val) => `${val.toFixed(1)}%` },
        },
        title: {
            text: "",
        },
    };

    return (
        <Box sx={{ width: "100%", mt: 4, position: "relative" }}>
            <Card sx={{
                width: "100%",
                boxShadow: "0 8px 28px rgba(46, 125, 50, 0.12), 0 2px 6px rgba(76, 175, 80, 0.08)",
                borderRadius: "16px",
                border: "1px solid rgba(76, 175, 80, 0.2)",
                overflow: "hidden",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #4caf50, #81c784, #a5d6a7)",
                },
            }}>
                <CardContent sx={{ pt: 3 }}>
                    <Typography gutterBottom align="center" sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#2e7d32",
                        letterSpacing: "0.4px",
                    }}>
                        Aproveitamento por Semana
                    </Typography>
                    {loadingProdDia || !datas ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                            <CircularProgress
                                size={40}
                                sx={{ color: "#4caf50" }}
                            />
                        </Box>
                    ) : (
                        <ReactApexChart
                            options={chartOptionsMult}
                            series={[
                                {
                                    name: "Aproveitamento",
                                    type: "column",
                                    data: datas ? datas.map((item) => item.produtividade) : [],
                                },
                            ]}
                            type="bar"
                            height={350}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default ProdutividadeSemana;
